import {
  buildGalleryDoc,
  GALLERY,
  GALLERY_CATEGORIES,
  SIZE_PRESETS,
  type GalleryCategory,
  type GalleryEntry,
  type LocaleId,
  type SizePreset,
  type TemplateDoc,
} from '@bannermatic/core';

/**
 * Pages generated from data: one per gallery template, per template category and per canvas
 * size preset. Plain data and helpers (no components) so the language detection can read the
 * addresses at start-up.
 */

/**
 * Templates shown on public pages. Templates with a stock photo placeholder are left out: their
 * pictures come from the photo service for signed-in users only.
 */
export const PUBLIC_TEMPLATES: GalleryEntry[] = GALLERY.filter((e) => !e.photo);

export const PUBLIC_CATEGORIES: GalleryCategory[] = GALLERY_CATEGORIES.filter((c) =>
  PUBLIC_TEMPLATES.some((e) => e.category === c),
);

export const TEMPLATES_PATH = '/templates';
export const categoryPath = (c: GalleryCategory) => `${TEMPLATES_PATH}/${c}`;
export const templatePath = (e: GalleryEntry) => `${TEMPLATES_PATH}/${e.category}/${e.id}`;

export const templatesIn = (c: GalleryCategory) => PUBLIC_TEMPLATES.filter((e) => e.category === c);

/**
 * Template name for pages and titles. Some templates share a name across sizes (a match day
 * post, story and wide banner); those get their size so every page has its own title.
 */
export function templateName(e: GalleryEntry, locale: LocaleId): string {
  const name = e.name[locale];
  const shared = PUBLIC_TEMPLATES.some((o) => o !== e && o.name[locale] === name);
  return shared ? `${name} ${e.width}×${e.height}` : name;
}

export function findPublicTemplate(id: string): GalleryEntry | undefined {
  return PUBLIC_TEMPLATES.find((e) => e.id === id);
}

/** A layer that data can fill: its name is the column / API field. */
export interface TemplateField {
  name: string;
  type: 'text' | 'image' | 'qr';
  example: string;
}

/** Text, image and QR layers of a template, in document order (stack children included). */
export function templateFields(doc: TemplateDoc): TemplateField[] {
  const out: TemplateField[] = [];
  for (const l of doc.layers) {
    if (l.type === 'text') out.push({ name: l.name, type: 'text', example: l.text });
    else if (l.type === 'image')
      out.push({ name: l.name, type: 'image', example: `https://cdn.example.com/${l.name}.jpg` });
    else if (l.type === 'qr') out.push({ name: l.name, type: 'qr', example: l.value });
  }
  // Decorative glyphs (a quote mark) are not something people fill in
  return out.filter((f) => f.type !== 'text' || f.example.trim().length > 1);
}

const csvCell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

/** A ready CSV for the template: its fields as the header and the template texts as a row. */
export function templateCsv(fields: TemplateField[]): string {
  return [
    fields.map((f) => f.name).join(','),
    fields.map((f) => csvCell(f.example.replace(/\n/g, ' '))).join(','),
  ].join('\n');
}

/** The same fields as an API request body. */
export function templateApiBody(fields: TemplateField[]): string {
  const layers = Object.fromEntries(fields.map((f) => [f.name, f.example.replace(/\n/g, ' ')]));
  return JSON.stringify({ template_id: 'tpl_…', format: 'png', layers }, null, 2);
}

/** Template document in a language. */
export const templateDoc = (e: GalleryEntry, locale: LocaleId) => buildGalleryDoc(e, locale);

/** Sizes a template is shown in besides its own (social formats only: ads keep their size). */
export const SHOWCASE_SIZES: Array<[number, number]> = [
  [1080, 1080],
  [1080, 1350],
  [1080, 1920],
  [1200, 628],
];

export function alternateSizes(e: GalleryEntry): Array<[number, number]> {
  if (e.category === 'ads') return [];
  const ratio = e.width / e.height;
  return SHOWCASE_SIZES.filter(([w, h]) => Math.abs(w / h - ratio) > 0.05).slice(0, 3);
}

// ---------------------------------------------------------------- sizes

export const SIZES_PATH = '/sizes';

/** Address of a size page: words people search for plus the dimensions. */
export const SIZE_SLUGS: Record<string, string> = {
  'ig-post': 'instagram-post-1080x1080',
  'ig-portrait': 'instagram-portrait-1080x1350',
  'ig-story': 'stories-reels-1080x1920',
  'fb-post': 'facebook-post-1200x630',
  'fb-cover': 'facebook-cover-1640x624',
  'x-post': 'x-twitter-post-1600x900',
  'linkedin-post': 'linkedin-post-1200x627',
  'telegram-post': 'telegram-post-1280x720',
  pinterest: 'pinterest-pin-1000x1500',
  'og-image': 'open-graph-image-1200x630',
  'web-hero': 'website-hero-1920x1080',
  'email-header': 'email-header-600x200',
  'yt-thumb': 'youtube-thumbnail-1280x720',
  'ad-mrec': 'medium-rectangle-300x250',
  'ad-leaderboard': 'leaderboard-728x90',
  'ad-skyscraper': 'wide-skyscraper-160x600',
  'ad-half-page': 'half-page-300x600',
  'ad-billboard': 'billboard-970x250',
  'ad-mobile': 'mobile-banner-320x50',
  'ad-mobile-large': 'large-mobile-banner-320x100',
  'a4-portrait': 'a4-portrait-1240x1754',
  'a4-landscape': 'a4-landscape-1754x1240',
  'business-card': 'business-card-1050x600',
};

export const SIZE_GROUPS = ['social', 'ads', 'web', 'video', 'print'] as const;
export type SizeGroup = (typeof SIZE_GROUPS)[number];

export const PUBLIC_SIZES: SizePreset[] = SIZE_PRESETS.filter((p) => SIZE_SLUGS[p.id]);
export const sizePath = (p: SizePreset) => `${SIZES_PATH}/${SIZE_SLUGS[p.id]}`;
export const sizesIn = (g: SizeGroup) => PUBLIC_SIZES.filter((p) => p.group === g);

function gcd(a: number, b: number): number {
  return b ? gcd(b, a % b) : a;
}

/** "16:9", or "1.91:1" when the exact ratio has big numbers. */
export function aspectRatio(w: number, h: number): string {
  const d = gcd(w, h);
  const [a, b] = [w / d, h / d];
  if (a <= 32 && b <= 32) return `${a}:${b}`;
  const r = w / h;
  return r >= 1 ? `${+r.toFixed(2)}:1` : `1:${+(h / w).toFixed(2)}`;
}

export const orientationOf = (w: number, h: number) =>
  Math.abs(w / h - 1) < 0.02 ? 'square' : w > h ? 'landscape' : 'portrait';

/**
 * Templates that look good laid out at a size: wide banners come from wide templates, tall from
 * tall. The gallery template with the closest aspect ratio wins.
 */
const SIZE_SOURCES = [
  'sale-landscape',
  'travel-banner',
  'headphones-promo',
  'summer-sale',
  'new-collection',
  'black-friday-story',
  'sale-story',
  'leaderboard',
  'billboard',
  'medium-rectangle',
  'half-page',
  'online-course',
];

export function templateForSize(p: SizePreset): GalleryEntry {
  const target = Math.log(p.width / p.height);
  const candidates = SIZE_SOURCES.map((id) => PUBLIC_TEMPLATES.find((e) => e.id === id)).filter(
    (e): e is GalleryEntry => !!e,
  );
  return candidates.reduce((best, e) =>
    Math.abs(Math.log(e.width / e.height) - target) <
    Math.abs(Math.log(best.width / best.height) - target)
      ? e
      : best,
  );
}
